import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import { loanService } from '../../../services/loanService';

interface LoanData {
  id: string;
  memberId: string;
  memberName: string;
  memberEmail?: string;
  loanAmount: number;
  remainingBalance: number;
  totalPaid: number;
  monthlyPayment: number;
  nextPaymentDate?: string;
  status: string;
  loanRequestId?: string;
}

interface LendingOverviewProps {
  groupId?: string;
}

const LendingOverview = ({ groupId }: LendingOverviewProps) => {
  const { user, userProfile } = useAuth();
  const [loans, setLoans] = useState<LoanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalLending, setTotalLending] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [cashInHand, setCashInHand] = useState<number | null>(null);

  const isSuperAdmin = userProfile?.role === 'super_admin';
  const platformRole = (userProfile?.role || '').toString();
  const groupRoles = userProfile?.group_roles || [];
  const canSeeCashInHand =
    platformRole === 'super_admin' ||
    platformRole === 'group_admin' ||
    platformRole === 'group_creator' ||
    groupRoles.includes('admin') ||
    groupRoles.includes('clerk');

  useEffect(() => {
    if (!user) return;
    fetchLoans();
  }, [user, groupId, isSuperAdmin, userProfile]);

  useEffect(() => {
    const loadCash = async () => {
      if (!groupId) {
        setCashInHand(null);
        return;
      }
      const cash = await loanService.getGroupCashInHand(groupId);
      setCashInHand(cash);
    };
    loadCash();
  }, [groupId]);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching loans...', { userId: user?.id, isSuperAdmin, groupId });

      // First, try to fetch from 'loans' table (if it exists)
      let loansData: LoanData[] = [];
      let totalLent = 0;
      let totalOut = 0;

      try {
        // First, check if loans table exists and is accessible
        const { data: tableCheck, error: tableError } = await supabase
          .from('loans')
          .select('id')
          .limit(1);
        
        if (tableError) {
          console.error('Loans table access error:', tableError);
          // If table doesn't exist or RLS blocks, try loan_requests
          console.log('Falling back to loan_requests table...');
        } else {
          console.log('Loans table is accessible, found', tableCheck?.length || 0, 'sample records');
        }

        // Try fetching from 'loans' table first
        let activeLoans: any[] = [];
        let loansError: any = null;

        // First try with foreign key join
        // Try different foreign key names that might exist
        let loansWithMember: any[] = [];
        let loansError1: any = null;

        // Try the most common foreign key name first
        const { data: loans1, error: error1 } = await supabase
          .from('loans')
          .select(`
            id,
            member_id,
            principal_amount,
            remaining_balance,
            monthly_payment,
            next_payment_date,
            status,
            group_id,
            total_amount,
            created_at,
            member:user_profiles!loans_member_id_fkey (
              id,
              full_name,
              email
            )
          `)
          .order('created_at', { ascending: false });

        if (error1) {
          // Try alternative foreign key name
          const { data: loans2, error: error2 } = await supabase
            .from('loans')
            .select(`
              id,
              member_id,
              principal_amount,
              remaining_balance,
              monthly_payment,
              next_payment_date,
              status,
              group_id,
              total_amount,
              created_at,
              member:user_profiles!member_id (
                id,
                full_name,
                email
              )
            `)
            .order('created_at', { ascending: false });

          if (error2) {
            loansError1 = error2;
          } else {
            loansWithMember = loans2 || [];
          }
        } else {
          loansWithMember = loans1 || [];
        }

        if (loansError1) {
          console.warn('Error with foreign key join, trying without:', loansError1);
          // Fallback: fetch loans without join
          const { data: loansOnly, error: loansError2 } = await supabase
            .from('loans')
            .select('id, member_id, principal_amount, remaining_balance, monthly_payment, next_payment_date, status, group_id, total_amount, created_at')
            .order('created_at', { ascending: false });
          
          if (loansError2) {
            loansError = loansError2;
            console.error('Error fetching loans:', loansError2);
            // If loans table doesn't exist or has RLS issues, try loan_requests
            console.log('Attempting to fetch from loan_requests as fallback...');
          } else {
            activeLoans = loansOnly || [];
          }
        } else {
          activeLoans = loansWithMember || [];
        }
        
        console.log('Loans fetched from loans table:', activeLoans.length);

        if (!loansError && activeLoans && activeLoans.length > 0) {
          // Fetch member profiles separately if not included
          const memberIds = [...new Set(activeLoans.map((l: any) => l.member_id))];
          let memberProfiles: Map<string, any> = new Map();
          
          if (memberIds.length > 0) {
            const { data: profiles, error: profilesError } = await supabase
              .from('user_profiles')
              .select('id, full_name, email')
              .in('id', memberIds);
            
            if (!profilesError && profiles) {
              profiles.forEach((profile: any) => {
                memberProfiles.set(profile.id, profile);
              });
            }
          }

          // Also fetch loan payments to calculate total paid
          const loanIds = activeLoans.map((l: any) => l.id);
          let payments: any[] = [];
          
          if (loanIds.length > 0) {
            const { data: paymentsData, error: paymentsError } = await supabase
              .from('loan_payments')
              .select('loan_id, amount')
              .in('loan_id', loanIds);
            
            if (paymentsError) {
              console.error('Error fetching loan payments:', paymentsError);
            } else {
              payments = paymentsData || [];
            }
          }

          loansData = activeLoans.map((loan: any) => {
            // Try to get member from join first, then from separate fetch
            let member = loan.member;
            if (!member && loan.member_id) {
              member = memberProfiles.get(loan.member_id);
            }
            
            const loanPayments = payments?.filter((p: any) => p.loan_id === loan.id) || [];
            const totalPaid = loanPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);

            return {
              id: loan.id,
              memberId: loan.member_id,
              memberName: member?.full_name || 'Unknown Member',
              memberEmail: member?.email,
              loanAmount: Number(loan.principal_amount || 0),
              remainingBalance: Number(loan.remaining_balance || 0),
              totalPaid,
              monthlyPayment: Number(loan.monthly_payment || 0),
              nextPaymentDate: loan.next_payment_date,
              status: loan.status || 'active',
              group_id: loan.group_id // Include group_id for filtering
            } as LoanData & { group_id?: string };
          });
        }
      } catch (err) {
        console.error('Error in loans fetch:', err);
      }

      // If no loans from 'loans' table, try 'loan_requests' with approved status
      if (loansData.length === 0) {
        try {
          const { data: approvedRequests, error: requestsError } = await supabase
            .from('loan_requests')
            .select(`
              id,
              member_id,
              requested_amount,
              status,
              monthly_payment_amount,
              member:user_profiles!loan_requests_member_id_fkey (
                id,
                full_name,
                email
              )
            `)
            .in('status', ['approved', 'disbursed']);

          if (requestsError) {
            console.error('Error fetching loan requests:', requestsError);
          }

          if (!requestsError && approvedRequests && approvedRequests.length > 0) {
            loansData = approvedRequests.map((request: any) => {
              const member = request.member as any;
              return {
                id: request.id,
                memberId: request.member_id,
                memberName: member?.full_name || 'Unknown Member',
                memberEmail: member?.email,
                loanAmount: Number(request.requested_amount || 0),
                remainingBalance: Number(request.requested_amount || 0), // Assume full amount if no payments tracked
                totalPaid: 0,
                monthlyPayment: Number(request.monthly_payment_amount || 0),
                status: request.status || 'approved',
                loanRequestId: request.id
              };
            });
          }
        } catch (err) {
          console.error('Error fetching loan requests:', err);
        }
      }

      // Filter by group if user is not super admin
      if (!isSuperAdmin && user) {
        // Get groups where user is admin or creator
        const { data: adminGroups } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          // group_members.role is a group-scoped role and does NOT include platform roles like 'group_admin'.
          .in('role', ['admin', 'clerk']);

        // Also check if user is creator of any groups
        const { data: createdGroups } = await supabase
          .from('groups')
          .select('id')
          .eq('created_by', user.id);

        const groupIds = new Set<string>();
        if (adminGroups) {
          adminGroups.forEach(g => groupIds.add(g.group_id));
        }
        if (createdGroups) {
          createdGroups.forEach(g => groupIds.add(g.id));
        }

        console.log('User groups for loan filtering:', { 
          adminGroups: adminGroups?.length || 0, 
          createdGroups: createdGroups?.length || 0,
          groupIds: Array.from(groupIds),
          userId: user.id
        });

        if (groupIds.size > 0) {
          // First, filter loans that have group_id
          const loansWithGroupId = loansData.filter(loan => {
            const loanGroupId = (loan as any).group_id;
            return loanGroupId && groupIds.has(loanGroupId);
          });

          // For loans without group_id, check if member is in any of the user's groups
          const loansWithoutGroupId = loansData.filter(loan => !(loan as any).group_id);
          
          if (loansWithoutGroupId.length > 0) {
            const { data: groupMembers } = await supabase
              .from('group_members')
              .select('user_id')
              .in('group_id', Array.from(groupIds));

            if (groupMembers) {
              const memberIds = new Set(groupMembers.map(m => m.user_id));
              const filteredLoansWithoutGroupId = loansWithoutGroupId.filter(loan => 
                memberIds.has(loan.memberId)
              );
              loansData = [...loansWithGroupId, ...filteredLoansWithoutGroupId];
            } else {
              // If can't fetch members, only show loans with group_id
              loansData = loansWithGroupId;
            }
          } else {
            loansData = loansWithGroupId;
          }
        } else {
          // If no admin groups found, show empty
          console.warn('No groups found for user, showing empty loans');
          loansData = [];
        }
      }

      // Calculate totals (only for active loans)
      const activeLoans = loansData.filter(loan => loan.status === 'active' || loan.status === 'defaulted' || loan.status === 'disbursed');
      totalLent = loansData.reduce((sum, loan) => sum + loan.loanAmount, 0);
      totalOut = activeLoans.reduce((sum, loan) => sum + loan.remainingBalance, 0);
      
      console.log('Loan calculations:', {
        totalLoans: loansData.length,
        activeLoans: activeLoans.length,
        totalLent,
        totalOut,
        loans: loansData.map(l => ({ id: l.id, member: l.memberName, amount: l.loanAmount, status: l.status }))
      });

      console.log('Loans fetched:', { 
        count: loansData.length, 
        totalLent, 
        totalOut, 
        loans: loansData,
        isSuperAdmin,
        userId: user?.id,
        groupId
      });

      setLoans(loansData);
      setTotalLending(totalLent);
      setTotalOutstanding(totalOut);
      
      // If no loans found, log for debugging
      if (loansData.length === 0) {
        console.warn('No loans found. Check RLS policies and group membership.');
      }
    } catch (err: any) {
      console.error('Error fetching loans:', err);
      setError(err.message || 'Failed to load loan data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VT`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-muted-foreground">Loading lending data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-md p-6">
        <div className="flex items-center gap-3 text-destructive mb-4">
          <Icon name="AlertCircle" size={20} />
          <p className="text-sm">{error}</p>
        </div>
        <button
          onClick={() => fetchLoans()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-muted-foreground">Total Lending</h3>
            <Icon name="TrendingUp" size={20} className="text-primary" />
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(totalLending)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {loans.length} active {loans.length === 1 ? 'loan' : 'loans'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-muted-foreground">Outstanding Balance</h3>
            <Icon name="DollarSign" size={20} className="text-warning" />
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(totalOutstanding)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatCurrency(totalLending - totalOutstanding)} repaid
          </p>
        </div>

        {canSeeCashInHand && (
          <div className="bg-card border border-border rounded-md p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-muted-foreground">Cash in Hand</h3>
              <Icon name="Wallet" size={20} className="text-success" />
            </div>
            <p className="text-2xl font-bold text-foreground">
              {formatCurrency(Number(cashInHand ?? 0))}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Group cash available (after outstanding group loans)
            </p>
          </div>
        )}
      </div>

      {/* Active Loans Table */}
      {(() => {
        const activeLoans = loans.filter(loan => loan.status === 'active' || loan.status === 'defaulted' || loan.status === 'disbursed');
        return (
          <div className="bg-card border border-border rounded-md">
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Active Loans</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Members with outstanding loans and payment details
              </p>
            </div>

            {activeLoans.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No active loans found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Member
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Loan Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Amount Paid
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Remaining Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Monthly Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Next Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {activeLoans.map((loan) => (
                      <tr key={loan.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-foreground">{loan.memberName}</div>
                            {loan.memberEmail && (
                              <div className="text-xs text-muted-foreground">{loan.memberEmail}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground font-medium">
                            {formatCurrency(loan.loanAmount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {formatCurrency(loan.totalPaid)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-semibold text-warning">
                            {formatCurrency(loan.remainingBalance)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-foreground">
                            {loan.monthlyPayment > 0 ? formatCurrency(loan.monthlyPayment) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-muted-foreground">
                            {formatDate(loan.nextPaymentDate)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            loan.status === 'active' || loan.status === 'disbursed'
                              ? 'bg-success/10 text-success'
                              : loan.status === 'defaulted'
                              ? 'bg-destructive/10 text-destructive'
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {loan.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      {/* Completed Loans History */}
      {(() => {
        const completedLoans = loans.filter(loan => loan.status === 'completed' || loan.status === 'repaid');
        if (completedLoans.length === 0) return null;
        
        return (
          <div className="bg-card border border-border rounded-md">
            <div className="p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Loan History</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Completed and repaid loans with full payment history
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Loan Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Total Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {completedLoans.map((loan) => (
                    <tr key={loan.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-foreground">{loan.memberName}</div>
                          {loan.memberEmail && (
                            <div className="text-xs text-muted-foreground">{loan.memberEmail}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-foreground font-medium">
                          {formatCurrency(loan.loanAmount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-success font-semibold">
                          {formatCurrency(loan.totalPaid)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                          {loan.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default LendingOverview;

